import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import { getUserInitials, useUserLoggedIn } from '~/modules/user';

/**
 * Identity circle at the head of the staff action row: whoever is holding these
 * tools. ArchiPro staff see their own avatar; a client sees their company logo,
 * since the tools only ever appear on their own items.
 *
 * Renders nothing when signed out, so the row keeps its three actions rather
 * than showing an empty circle.
 */
export const StaffAvatarButton = () => {
    const { user, isLoggedIn } = useUserLoggedIn();
    // An image that 404s would otherwise sit as a blank circle: track the failure
    // so the initials fallback takes over.
    const [imageFailed, setImageFailed] = useState(false);

    if (!isLoggedIn) return null;

    // Staff are themselves; a client is their business. Their first professional
    // is the one whose items carry these buttons.
    const professional = user.IsArchiproStaff
        ? undefined
        : user.Professionals?.find((entry) => entry?.CompanyLogo);
    const source = professional?.CompanyLogo ?? user.ProfileImage;
    const label = professional?.Title ?? user.Name;

    return (
        <Avatar
            // square48 lands at the same rendered size as the action buttons
            // beside it, which are sized in absolute px by Aria.
            size="square48"
            title={label}
            // A logo is artwork on its own background, so show it whole rather
            // than cropping it to fill the circle like a photo.
            style={
                professional
                    ? {
                          backgroundColor:
                              professional.LogoBackgroundColor ?? 'white',
                      }
                    : undefined
            }
        >
            {source && !imageFailed && (
                <AvatarImage
                    src={source}
                    alt={label}
                    fit={professional ? 'contain' : 'cover'}
                    onError={() => setImageFailed(true)}
                />
            )}
            <AvatarFallback size="xs">{getUserInitials(user)}</AvatarFallback>
        </Avatar>
    );
};
